import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
    Box,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PrimaryBtn from '../shared/Buttons/PrimaryButton';
import useDefaultStockSWR from '../../hooks/useDefaultStock';
import SnackbarUtils from '../../utils/SnackbarUtils';
import {
    ProfileFormProps,
    ProfileFormValues,
    ProfileRequest,
} from '../../models/User';
import useUser from 'hooks/user/useUser';
import DeleteButton from 'components/shared/Buttons/DeleteButton';
import useAuth from '../../hooks/authentication/useAuth';
import useConfirmDialog from '../../hooks/dialogs/useConfirmDialog';
import useProfileEditorDialog from '../../hooks/useProfileEditorDialog';
import TextFieldStyled from '../shared/TextFieldStyled';
import { FormControlsWrapper } from '../styled/Form/FormControlsWrapper';
import SelectStyled from '../shared/SelectStyled';
import useFullScreenLoading from '../../hooks/useFullScreenLoading';
import { Form } from '@shopify/polaris';
import CheckboxStyled from 'components/shared/CheckboxCustom';

const schema = yup.object().shape({
    // first_name: yup
    //     .string()
    //     .required('FORM_VALIDATION_ERROR.REQUIRED')
    //     .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    // last_name: yup
    //     .string()
    //     .required('FORM_VALIDATION_ERROR.REQUIRED')
    //     .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    // company: yup
    //     .string()
    //     .required('FORM_VALIDATION_ERROR.REQUIRED')
    //     .min(3, 'FORM_VALIDATION_ERROR.MIN_CHARACTER_3'),
    // phone: yup
    //     .string()
    //     .required('FORM_VALIDATION_ERROR.REQUIRED')
    //     .matches(PATTERNS.PHONE_NUMBER, 'FORM_VALIDATION_ERROR.PHONE'),
    // tax_number: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
    default_stock: yup.string().required('FORM_VALIDATION_ERROR.REQUIRED'),
});

export const ProfileForm: React.FC<ProfileFormProps> = (props) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm<ProfileFormValues>({
        resolver: yupResolver(schema),
    });
    const profileDialog = useProfileEditorDialog();
    const user = profileDialog.selectedUserStates;
    const [defaultStock, setDefaultStock] = useState(
        user ? user.default_stock : ''
    );
    const { stocks, isLoadingStock, isErrorStock } = useDefaultStockSWR();
    const useProfile = useUser();
    const userUse = useUser();
    const auth = useAuth();
    const confirmDialog = useConfirmDialog();
    const { t } = useTranslation();
    const fullScreenLoading = useFullScreenLoading();

    const [submitIsLoading, setSubmitIsLoading] = useState(false);

    const handleDeleteProfile = () => {
        userUse.deleteUser().then(() => {
            SnackbarUtils.success('PROFILE.PROFILE_DELETE');
            props.submitSuccess();
            auth.logout();
        });
    };

    const deleteProfile = () => {
        confirmDialog.showDialog({
            title: t('PROFILE.PROFILE_DELETE_CONFIRM_DIALOG.TITLE'),
            content: t('PROFILE.PROFILE_DELETE_CONFIRM_DIALOG.CONTENT'),
            confirmText: t('PROFILE.PROFILE_DELETE_CONFIRM_DIALOG.CONFIRM'),
            cancelText: t('PROFILE.PROFILE_DELETE_CONFIRM_DIALOG.CANCEL'),
            onConfirm: () => handleDeleteProfile(),
        });
    };

    // Backend need default values to save it... Only default stock can change
    const makeRequestBody = (data: ProfileFormValues): ProfileRequest => {
        return {
            first_name: user.first_name,
            last_name: user.last_name,
            company: user.company,
            phone: user.phone,
            tax_number: user.tax_number,
            default_stock: data.default_stock,
            default_profit_margin: data.default_profit_margin,
            use_logo: data.use_logo,
        };
    };

    const onSubmit: SubmitHandler<ProfileFormValues> = (data) => {
        setSubmitIsLoading(true);
        fullScreenLoading.setFullScreenLoading(true);
        useProfile
            .updateUser(makeRequestBody(data))
            .then(() => {
                SnackbarUtils.success(
                    t('NOTIFICATION.PROFILE_EDITED_SUCCESSFULLY').toString()
                );
                // mutate();
                props.submitSuccess();
            })
            .catch(() => {
                setSubmitIsLoading(false);
            })
            .finally(() => {
                fullScreenLoading.setFullScreenLoading(false);
            });
    };

    const getTranslate = (key) => {
        return t(key).toString();
    };

    const handleChange = (event: SelectChangeEvent) => {
        setDefaultStock(event.target.value as string);
    };

    if (!user || isLoadingStock || isErrorStock) {
        return <></>;
    }

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <>
                    {/* Email address */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled
                            type="email"
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.EMAIL'
                            ).toString()}
                            defaultValue={user.email}
                            {...register('emailAddress')}
                            helperText={' '}
                        />
                    </FormControl>

                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.COMPANY'
                            ).toString()}
                            defaultValue={user.company}
                            error={!!errors.company}
                            {...register('company')}
                            helperText={
                                errors.company
                                    ? getTranslate(errors.company.message)
                                    : ' '
                            }
                        />
                    </FormControl>

                    <FormControlsWrapper>
                        {/* Last Name */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                disabled
                                label={t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.LAST_NAME'
                                ).toString()}
                                defaultValue={user.last_name}
                                error={!!errors.last_name}
                                {...register('last_name')}
                                helperText={
                                    errors.last_name
                                        ? getTranslate(errors.last_name.message)
                                        : ' '
                                }
                            />
                        </FormControl>
                        {/* First Name */}
                        <FormControl fullWidth>
                            <TextFieldStyled
                                disabled
                                label={t(
                                    'PROFILE.PROFILE_DIALOG.FORM_CONTENT.FIRST_NAME'
                                ).toString()}
                                defaultValue={user.first_name}
                                error={!!errors.first_name}
                                {...register('first_name')}
                                helperText={
                                    errors.first_name
                                        ? getTranslate(
                                              errors.first_name.message
                                          )
                                        : ' '
                                }
                            />
                        </FormControl>
                    </FormControlsWrapper>
                    {/* Phone */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.PHONE'
                            ).toString()}
                            defaultValue={user.phone}
                            error={!!errors.phone}
                            {...register('phone')}
                            helperText={
                                errors.phone
                                    ? getTranslate(errors.phone.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* Tax number */}
                    <FormControl fullWidth>
                        <TextFieldStyled
                            disabled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.TAX_NUMBER'
                            ).toString()}
                            defaultValue={user.tax_number}
                            error={!!errors.tax_number}
                            {...register('tax_number')}
                            helperText={
                                errors.tax_number
                                    ? getTranslate(errors.tax_number.message)
                                    : ' '
                            }
                        />
                    </FormControl>
                    {/* Default stock */}
                    <FormControl fullWidth variant="filled">
                        <InputLabel
                            sx={{
                                color: '#999999',
                            }}
                        >
                            {t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.DEFAULT_STOCK'
                            ).toString()}
                        </InputLabel>
                        <SelectStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.DEFAULT_STOCK'
                            ).toString()}
                            {...register('default_stock', {
                                onChange: (e) => handleChange(e),
                            })}
                            value={defaultStock}
                        >
                            {stocks.map((stock) => (
                                <MenuItem
                                    key={stock.bc_location_code}
                                    value={stock.bc_location_code}
                                >
                                    {stock.bc_location_name}
                                </MenuItem>
                            ))}
                        </SelectStyled>
                        <FormHelperText error={!!errors.default_stock}>
                            &#8203;
                            {errors.default_stock &&
                                errors.default_stock.message}
                        </FormHelperText>
                    </FormControl>
                    <FormControl fullWidth>
                        <TextFieldStyled
                            label={t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.DEFAULT_PROFIT_MARGIN'
                            ).toString()}
                            defaultValue={user.default_profit_margin}
                            {...register('default_profit_margin')}
                            helperText={' '}
                            type={'number'}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                    </FormControl>
                    <FormControl fullWidth>
                        <Controller
                            name="use_logo"
                            control={control}
                            defaultValue={user.use_logo}
                            render={({ field }) => (
                                <FormControlLabel
                                    label={t(
                                        'PROFILE.PROFILE_DIALOG.FORM_CONTENT.USER_LOGO'
                                    )}
                                    sx={{ paddingLeft: '12px' }}
                                    control={
                                        <CheckboxStyled
                                            onChange={(e) => {
                                                field.onChange(e);
                                            }}
                                            checked={!!field.value}
                                        />
                                    }
                                />
                            )}
                        />
                    </FormControl>
                </>
                <Box
                    sx={{
                        justifyContent: 'space-between',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ flex: 1 }} />
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <PrimaryBtn type="submit" disabled={submitIsLoading}>
                            {t(
                                'PROFILE.PROFILE_DIALOG.FORM_CONTENT.SEND'
                            ).toString()}
                        </PrimaryBtn>
                    </Box>
                    <Box
                        sx={{ flex: 1, display: 'flex', justifyContent: 'end' }}
                    >
                        <DeleteButton onClick={deleteProfile}>
                            {t('PROFILE.ADDRESS_CONTENT.DELETE_PROFILE')}
                        </DeleteButton>
                    </Box>
                </Box>
            </form>
        </>
    );
};
